import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const EVENT = "rj:start-tour";
export const startTour = () => window.dispatchEvent(new Event(EVENT));

const nav = (name) => [`nav-${name}`, `mobile-nav-${name}`];

const STEPS = [
  { title: "Welcome to Rockafeller Jones", body: "Your market dashboard with live prices, portfolio tracking, and Woodrow, an AI trader that runs a $100,000 simulated portfolio for you. Here's a 60-second tour. Skip it any time." },
  { targets: ["portfolio-summary"], title: "Your money at a glance", body: "Your own picks and Woodrow's portfolio, combined, with a 30-day value trend. No real money is ever at risk." },
  { targets: [...nav("markets"), "all-stocks-panel"], title: "Portfolio & markets", body: "Your home page: live prices for 110+ major stocks, sector performance and the day's top movers. Click any stock for real price charts and news." },
  { targets: nav("crypto"), title: "Crypto", body: "Bitcoin, Ethereum and the other top coins, trading 24/7, even when the stock market is closed." },
  { targets: nav("portfolio"), title: "My Picks & limit orders", body: "Track your own stock picks, sell all or part of them, and set take-profit or stop-loss orders that fire automatically when a price is hit." },
  { targets: nav("woodrow"), title: "Meet Woodrow, your AI trader", body: "Woodrow analyses the market and trades your $100k simulated account on his own, with stop-losses, trailing stops and hedging when markets fall." },
  { targets: nav("woodrow"), title: "Tune Woodrow's strategy", body: "On the Woodrow page, pick his risk level and style (conservative to aggressive, stocks or crypto) and see every trade he makes and why." },
  { targets: nav("leaderboard"), title: "Leaderboard", body: "See how your Woodrow stacks up against other traders and the S&P 500. Only display names are shown." },
  { targets: nav("visualization"), title: "3D Visualization", body: "Explore every Woodrow trade in interactive 3D. Drag to rotate, and press Play to watch strategies evolve over time." },
  { targets: ["search-toggle-btn"], title: "Quick search", body: "Press / anywhere to search for a stock and jump straight to its chart." },
  { targets: ["theme-picker-btn", "profile-link", "mobile-profile-link"], title: "Make it yours", body: "Switch colour themes here, and pick a background like Wall Street Dusk or Gold Bull from your Profile." },
  { targets: ["tour-help-btn"], title: "You're all set", body: "Replay this tour any time from the ? button or your Profile. Happy investing!" },
];

const findTarget = (ids = []) => {
  for (const id of ids) {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
};

const PAD = 6;
const CARD_W = 340;

function cardPosition(rect) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - 24);
  if (!rect) return { left: (vw - w) / 2, top: Math.max(24, vh / 2 - 140), width: w };
  const left = Math.min(Math.max(12, rect.left + rect.width / 2 - w / 2), vw - w - 12);
  const H = 260;
  const below = rect.bottom + PAD + 14;
  if (below + H < vh) return { left, top: below, width: w };
  const above = rect.top - PAD - 14 - H;
  if (above > 12) return { left, top: above, width: w };
  const top = Math.min(Math.max(12, rect.top), vh - H - 12);
  if (rect.right + 20 + w < vw) return { left: rect.right + 20, top, width: w };
  if (rect.left - 20 - w > 0) return { left: rect.left - 20 - w, top, width: w };
  return { left, top: vh - H - 12, width: w };
}

const Dots = ({ i }) => (
  <div className="flex gap-1" data-testid="tour-progress">
    {STEPS.map((_, k) => (
      <span key={k} className={`h-1 transition-all duration-300 ${k === i ? "w-5 bg-bt-gold" : k < i ? "w-2 bg-bt-gold/50" : "w-2 bg-bt-border-subtle"}`} />
    ))}
  </div>
);

function TourCard({ i, pos, onNext, onBack, onSkip }) {
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  return (
    <div className="fixed z-[202] bg-bt-surface border border-bt-gold/50 shadow-2xl p-5 transition-[top,left] duration-300"
      style={pos} data-testid="tour-card" role="dialog" aria-label={step.title}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          {i === 0 && <Sparkles className="w-4 h-4 text-bt-gold" />}
          <h3 className="font-heading text-lg text-bt-text-primary leading-tight" data-testid="tour-title">{step.title}</h3>
        </div>
        <button onClick={onSkip} className="text-bt-text-tertiary hover:text-bt-text-primary" title="Close tour" data-testid="tour-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-bt-text-secondary leading-relaxed mb-4" data-testid="tour-body">{step.body}</p>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-3">
        <Dots i={i} />
        <div className="flex items-center gap-2 ml-auto">
          {!last && <button onClick={onSkip} className="text-xs font-mono text-bt-text-tertiary hover:text-bt-text-primary px-2 py-1.5 whitespace-nowrap" data-testid="tour-skip">Skip tour</button>}
          {i > 0 && (
            <button onClick={onBack} className="p-1.5 border border-bt-border-subtle text-bt-text-secondary hover:text-bt-text-primary" data-testid="tour-back" title="Back">
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onNext} className="flex items-center gap-1 px-3 py-1.5 bg-bt-gold text-bt-bg text-xs font-mono font-semibold hover:bg-bt-gold/80 whitespace-nowrap" data-testid="tour-next">
            {last ? "Finish" : i === 0 ? "Show me" : "Next"} {!last && <ArrowRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      <p className="mt-3 text-[10px] font-mono text-bt-text-tertiary" data-testid="tour-step-count">Step {i + 1} of {STEPS.length}</p>
    </div>
  );
}

export default function GuidedTour() {
  const { user, completeTour, isEmulating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [i, setI] = useState(null);
  const [rect, setRect] = useState(null);

  const open = useCallback(() => { if (location.pathname !== "/") navigate("/"); setI(0); }, [location.pathname, navigate]);

  useEffect(() => {
    window.addEventListener(EVENT, open);
    return () => window.removeEventListener(EVENT, open);
  }, [open]);

  useEffect(() => {
    if (user?.tour_pending && !user?.must_change_password && !user?.disclaimer_pending && !isEmulating() && i === null) open();
  }, [user?.tour_pending, user?.must_change_password, user?.disclaimer_pending]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (i === null) return;
    let raf;
    const measure = () => {
      const el = findTarget(STEPS[i].targets);
      const r = el ? el.getBoundingClientRect() : null;
      setRect(prev => (prev && r && prev.left === r.left && prev.top === r.top && prev.width === r.width && prev.height === r.height) || (!prev && !r) ? prev : r);
    };
    const el = findTarget(STEPS[i].targets);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    const loop = () => { measure(); raf = requestAnimationFrame(loop); };
    const t = setTimeout(loop, 50);
    return () => { clearTimeout(t); cancelAnimationFrame(raf); };
  }, [i]);

  useEffect(() => {
    if (i === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") setI(k => Math.max(0, k - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (i === null) return null;

  const finish = () => { setI(null); if (user?.tour_pending) completeTour(); };
  const next = () => (i >= STEPS.length - 1 ? finish() : setI(i + 1));
  const hole = rect && { left: rect.left - PAD, top: rect.top - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 };

  return (
    <div data-testid="guided-tour">
      <div className="fixed inset-0 z-[200]" onClick={(e) => e.stopPropagation()} />
      {hole ? (
        <div className="fixed z-[201] pointer-events-none border-2 border-bt-gold transition-all duration-300 ease-out"
          style={{ ...hole, boxShadow: "0 0 0 9999px rgba(0,0,0,0.62), 0 0 24px rgba(234,179,8,0.45)" }} data-testid="tour-spotlight" />
      ) : (
        <div className="fixed inset-0 z-[201] bg-black/60 pointer-events-none" />
      )}
      <TourCard i={i} pos={cardPosition(rect)} onNext={next} onBack={() => setI(Math.max(0, i - 1))} onSkip={finish} />
    </div>
  );
}
